import { useDeferredValue, useMemo, useState } from "react";
import { useLocalization } from "../../../hooks/useLocalization";
import { useMatches } from "../../../hooks/useMatches";
import { useTeamInfo } from "../../../hooks/useTeams";
import { canonicalMatchTypeKey, getMatchTypeLocalized } from "../../../services/utils/utils";
import { UpcomingMatch } from "../../../types/types";

const groupUpcomingByMatchType = (
  matches: UpcomingMatch[],
  strings: ReturnType<typeof useLocalization>["strings"],
) => {
  const sorted = [...matches].sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
  );

  const groups = sorted.reduce<
    Array<{ key: string; label: string; matches: UpcomingMatch[]; sortTime: number }>
  >((acc, match) => {
    const key = canonicalMatchTypeKey(match.matchType);
    const existing = acc.find((group) => group.key === key);

    if (existing) {
      existing.matches.push(match);
    } else {
      acc.push({
        key,
        label: getMatchTypeLocalized(key, strings),
        matches: [match],
        sortTime: new Date(match.time).getTime(),
      });
    }

    return acc;
  }, []);

  return groups
    .sort((a, b) => a.sortTime - b.sortTime)
    .map(({ key, label, matches: weekMatches }) => ({ key, label, matches: weekMatches }));
};

const matchIncludesTeam = (match: UpcomingMatch, teamIds: Set<string>) => {
  if (!teamIds.size) return true;
  return teamIds.has(match.teamOne ?? "") || teamIds.has(match.teamTwo ?? "");
};

export const useSchedule = () => {
  const { strings } = useLocalization();
  const { upcomingMatches, loading } = useMatches();
  const teamInfos = useTeamInfo();
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(() => new Set());
  const deferredTeamIds = useDeferredValue(selectedTeamIds);

  const teamsInSchedule = useMemo(() => {
    if (!teamInfos?.length || !upcomingMatches?.length) return [];

    const ids = new Set<string>();
    upcomingMatches.forEach((match) => {
      if (match.teamOne) ids.add(match.teamOne);
      if (match.teamTwo) ids.add(match.teamTwo);
    });

    return teamInfos
      .filter((team) => ids.has(team.id))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [teamInfos, upcomingMatches]);

  const filteredMatches = useMemo(() => {
    if (!upcomingMatches?.length) return [];
    return upcomingMatches.filter((match) => matchIncludesTeam(match, deferredTeamIds));
  }, [upcomingMatches, deferredTeamIds]);

  const groups = useMemo(
    () => (filteredMatches.length ? groupUpcomingByMatchType(filteredMatches, strings) : []),
    [filteredMatches, strings],
  );

  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  const clearTeamFilter = () => {
    setSelectedTeamIds(new Set());
  };

  const hasUpcoming = !!upcomingMatches?.length;
  const filterActive = deferredTeamIds.size > 0;

  return {
    strings,
    loading,
    selectedTeamIds,
    teamsInSchedule,
    groups,
    toggleTeam,
    clearTeamFilter,
    hasUpcoming,
    filterActive,
  };
};
